import { useMemo } from 'react';
import { PRODUCT_DISCOVERY_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import { BANNER_NAME } from '../constants.tsx';
import {
	useJpdBlueprintsFeedbackBannerActions,
	useJpdBlueprintsFeedbackBannerState,
} from './store/index.tsx';
import { useHasPolarisInPath } from './helpers.tsx';

export const useBannerJpdBlueprintsFeedback = () => {
	const hasPolarisInPath = useHasPolarisInPath();
	const { data: project } = useProjectContext();
	const accountId = useAccountId();
	const { isDismissed, height, shouldShow, isLoading } = useJpdBlueprintsFeedbackBannerState();
	const { fetchBannerVisibility } = useJpdBlueprintsFeedbackBannerActions();
	const isDiscoveryProject = project?.projectType === PRODUCT_DISCOVERY_PROJECT;

	const isInJpdBoundary = useMemo(
		() => hasPolarisInPath || isDiscoveryProject,
		[isDiscoveryProject, hasPolarisInPath],
	);

	useEffectWithoutConcurrency(() => {
		if (!fg('jpd-view-templates-feedback-banner')) {
			return;
		}

		if (!accountId) {
			return;
		}

		// Fetch only if we are in JPD boundary
		if (isInJpdBoundary) {
			fetchBannerVisibility(accountId, project?.projectId);
		}
	}, [accountId, fetchBannerVisibility, project, isInJpdBoundary]);

	return useMemo(
		() => ({
			isEnabled:
				isInJpdBoundary &&
				shouldShow &&
				!isDismissed &&
				!isLoading &&
				fg('jpd-view-templates-feedback-banner'),
			name: BANNER_NAME,
			height,
		}),
		[isInJpdBoundary, shouldShow, isDismissed, height, isLoading],
	);
};
